import { Typography } from "@cappelendamm/design-system/react";
import classNames from "classnames";
import Image from "next/image";
import { type ReactNode } from "react";
import { ArrowRight, BookStack, Clock, Xmark } from "iconoir-react";
import Contributor from "@/utils/contributor.svg";
import { ContainerLink } from "@/navigation/components/ContainerLink";

export type AutocompleteTemplateItemProps = {
  format?: string | null;
  language?: string | null;
  imageSrc?: string | null;
  altText: string;
  highlightName: React.ReactNode;
  highlightDetails?: React.ReactNode;
  price?: string | null;
  itemType?: "contributor" | "book" | "literary-work" | "series" | "article";
  href?: string;
  children?: React.ReactNode;
  className?: string;
};

export function AutocompleteTemplateItem({
  imageSrc,
  format,
  language,
  altText,
  highlightName,
  highlightDetails,
  children,
  href,
  className,
  itemType,
}: AutocompleteTemplateItemProps) {
  return (
    <ContainerLink link={{ href }} tabIndex={0}>
      <div
        className={classNames(
          "relative grid cursor-pointer grid-flow-col grid-cols-[48px_1fr_auto] items-center gap-x-6 px-2 py-2",
          className,
        )}
      >
        {imageSrc && (
          <Image
            src={imageSrc}
            alt={altText}
            height={24}
            width={48}
            className="min-w-12 justify-self-center object-contain"
          />
        )}
        {!imageSrc && itemType === "contributor" && (
          <Contributor className="aspect-square h-[48px] w-[48px] justify-self-center text-gray-200" />
        )}
        {!imageSrc && itemType !== "contributor" && (
          <div className="h-[48px] w-[34px] justify-self-center bg-gray-200" />
        )}
        <div className="flex h-fit flex-col gap-x-0 truncate sm:flex-row">
          <span className="h-5 truncate font-bold">{highlightName}</span>
          {highlightDetails && (
            <span className="truncate sm:before:mr-1 sm:before:content-[',']">
              {highlightDetails}
            </span>
          )}
          {format && (
            <span className="truncate sm:before:mr-1 sm:before:content-[',']">
              {format}
            </span>
          )}
          {language && (
            <span className="truncate sm:before:mr-1">({language})</span>
          )}
        </div>
        {children}
      </div>
    </ContainerLink>
  );
}

export function SourceHeader({
  children,
  action,
}: {
  children: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="mb-2 flex min-h-5 items-center justify-between gap-4">
      <Typography as="p" variant="label-sm" muted>
        {children}
      </Typography>
      {action}
    </div>
  );
}

function getSearchHref(query: string) {
  return `/search?query=${encodeURIComponent(query)}`;
}

export function ShowAllLink({
  query,
  className,
}: {
  query: string;
  className?: string;
}) {
  return (
    <ContainerLink
      link={{ href: getSearchHref(query) }}
      className={classNames(
        "items-center gap-1.5 cvr-label-md no-underline hover:underline",
        className,
      )}
    >
      Vis alle treff for «{query}»
      <ArrowRight width={18} height={18} aria-hidden />
    </ContainerLink>
  );
}

export function ShowAllButton({
  query,
  className,
}: {
  query: string;
  className?: string;
}) {
  return (
    <ContainerLink
      link={{ href: getSearchHref(query) }}
      className={classNames(
        "mt-4 flex h-11 items-center justify-center rounded-full border border-current cvr-label-lg no-underline",
        className,
      )}
    >
      Vis alle treff for «{query}»
    </ContainerLink>
  );
}

export type ProductCardProps = {
  href: string;
  imageSrc?: string | null;
  title: string;
  byline?: string | null;
  details?: string | null;
};

export function ProductCard({
  href,
  imageSrc,
  title,
  byline,
  details,
}: ProductCardProps) {
  return (
    <ContainerLink link={{ href }} className="grid gap-2.5 no-underline">
      <span className="relative block h-39 md:h-50">
        {imageSrc ? (
          <Image
            src={imageSrc}
            alt=""
            fill
            sizes="(max-width: 1023px) 104px, 160px"
            className="object-contain object-left-bottom"
          />
        ) : (
          <span className="absolute bottom-0 left-0 h-full w-3/4 bg-gray-200" />
        )}
      </span>
      <span className="grid gap-0.5">
        <span data-title className="line-clamp-2 cvr-label-md">
          {title}
        </span>
        {byline ? (
          <span className="truncate cvr-body-sm text-muted">{byline}</span>
        ) : null}
        {details ? (
          <span className="cvr-body-sm text-muted">{details}</span>
        ) : null}
      </span>
    </ContainerLink>
  );
}

function getInitials(name: string) {
  const words = name.split(/\s+/).filter(Boolean);
  const first = words.at(0)?.at(0) ?? "";
  const last = words.length > 1 ? (words.at(-1)?.at(0) ?? "") : "";
  return `${first}${last}`.toUpperCase();
}

export function Portrait({
  name,
  imageSrc,
}: {
  name: string;
  imageSrc?: string | null;
}) {
  if (imageSrc)
    return (
      <Image
        src={imageSrc}
        alt=""
        width={40}
        height={40}
        className="size-10 shrink-0 rounded-full object-cover md:size-9"
      />
    );
  return (
    <span
      aria-hidden
      className="flex size-10 shrink-0 items-center justify-center rounded-full bg-surface-100 cvr-label-sm md:size-9"
    >
      {getInitials(name)}
    </span>
  );
}

export function Thumbnail({ imageSrc }: { imageSrc?: string | null }) {
  if (imageSrc)
    return (
      <Image
        src={imageSrc}
        alt=""
        width={40}
        height={40}
        className="size-10 shrink-0 rounded-lg object-cover md:size-9"
      />
    );
  return (
    <span
      aria-hidden
      className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-surface-100 md:size-9"
    >
      <BookStack width={18} height={18} />
    </span>
  );
}

export function SearchRow({
  href,
  external,
  media,
  label,
}: {
  href: string;
  external?: boolean;
  media?: ReactNode;
  label: string;
}) {
  return (
    <ContainerLink
      link={{ href, external }}
      target={external ? "_blank" : undefined}
      className="flex min-h-12 items-center gap-3 px-2 no-underline md:min-h-11"
    >
      {media}
      <span className="min-w-0 truncate cvr-body-sm font-medium">{label}</span>
    </ContainerLink>
  );
}

export function RecentSearchRow({
  label,
  onRemove,
}: {
  label: string;
  onRemove: () => void;
}) {
  return (
    <span className="flex min-h-12 items-center gap-3 pl-2 md:min-h-9">
      <Clock width={18} height={18} aria-hidden className="shrink-0" />
      <span className="min-w-0 flex-1 truncate cvr-body-sm">{label}</span>
      <button
        type="button"
        aria-label={`Fjern «${label}» fra siste søk`}
        className="flex size-11 shrink-0 cursor-pointer items-center justify-center md:size-9"
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          onRemove();
        }}
      >
        <Xmark width={16} height={16} aria-hidden />
      </button>
    </span>
  );
}

export function NoResults({ query }: { query: string }) {
  return (
    <Typography as="p" variant="body-sm">
      Ingen treff for «{query}». Sjekk stavemåten eller prøv et annet ord.
    </Typography>
  );
}

export function TopAuthorCard({
  href,
  name,
  imageSrc,
}: {
  href: string;
  name: string;
  imageSrc?: string | null;
}) {
  return (
    <ContainerLink
      link={{ href }}
      className="flex items-center gap-4 rounded-2xl bg-surface-100 p-4 no-underline md:flex-col md:items-start md:gap-3 md:p-5"
    >
      {imageSrc ? (
        <Image
          src={imageSrc}
          alt=""
          width={80}
          height={80}
          className="size-14 shrink-0 rounded-full object-cover md:size-18"
        />
      ) : (
        <span
          aria-hidden
          className="flex size-14 shrink-0 items-center justify-center rounded-full bg-surface-0 cvr-label-lg md:size-18"
        >
          {getInitials(name)}
        </span>
      )}
      <span className="grid min-w-0 flex-1 gap-0.5">
        <span data-title className="cvr-title-sm">
          {name}
        </span>
        <span className="cvr-body-sm text-muted">Forfatter</span>
      </span>
      <span className="flex items-center gap-1.5 cvr-label-md">
        <span className="hidden md:inline">Se alle bøker</span>
        <ArrowRight width={18} height={18} aria-hidden />
      </span>
    </ContainerLink>
  );
}
